import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import FlagIcon from '../FlagIcon';
import { formatPaymentPurpose } from '../../utils/recipientHelpers';
import { useNumberFormat } from '../../hooks/useNumberFormat';

const CARD_CLASS = 'rounded-[14px] border border-[var(--color-border)] bg-[var(--white)] shadow-[var(--shadow-md)] overflow-hidden';
const LIMIT = 4;

const STATUS_DISPLAY = {
  Completed: { label: 'Settled', color: 'var(--color-success)' },
  Pending: { label: 'Pending', color: 'var(--color-warning)' },
  Executing: { label: 'In transit', color: '#2e6bad' },
  Processing: { label: 'In transit', color: '#2e6bad' },
  Failed: { label: 'Failed', color: 'var(--color-error)' },
};

function toTransactionList(transactions) {
  return Array.isArray(transactions) ? transactions : Object.values(transactions || {});
}

function formatSubtitle(transaction) {
  if (transaction.paymentReference) return transaction.paymentReference;
  return formatPaymentPurpose(transaction.paymentPurpose) || '—';
}

function formatOutgoingAmount(amount, currency, formatNumber) {
  const value = Number(amount) || 0;
  const formatted = formatNumber(value);
  const suffix = currency === 'EUR' ? '€' : currency;
  return `−${formatted} ${suffix}`;
}

function getStatusDisplay(status) {
  return STATUS_DISPLAY[status] || { label: status || '—', color: 'var(--text-tertiary)' };
}

function ActivityFeed({ transactions, loading }) {
  const navigate = useNavigate();
  const { formatNumber } = useNumberFormat();

  const items = useMemo(
    () =>
      toTransactionList(transactions)
        .filter((t) => t.started)
        .sort((a, b) => new Date(b.started) - new Date(a.started))
        .slice(0, LIMIT),
    [transactions],
  );

  if (!loading && items.length === 0) {
    return null;
  }

  return (
    <section>
      <div className='mb-3.5 flex items-center justify-between gap-3'>
        <p className='section-label'>Recent activity</p>
        <button
          type='button'
          onClick={() => navigate('/transaction-history')}
          className='text-xs font-medium text-[var(--n500)] hover:underline'
        >
          View all →
        </button>
      </div>

      <div className={CARD_CLASS}>
        {loading &&
          [0, 1, 2, 3].map((key) => (
            <div key={key} className={`h-[68px] animate-pulse bg-[var(--off)] ${key < 3 ? 'border-b border-[var(--color-border)]' : ''}`} />
          ))}

        {!loading &&
          items.map((transaction, index) => {
            const status = getStatusDisplay(transaction.status);
            const amountLabel = formatOutgoingAmount(transaction.amountIn, transaction.baseCurrency, formatNumber);
            const subtitle = formatSubtitle(transaction);

            return (
              <button
                key={transaction.requestId}
                type='button'
                onClick={() => navigate(`/transaction-history/${transaction.requestId}`)}
                className={`grid w-full grid-cols-[auto_1fr_auto_auto] items-center gap-4 px-[22px] py-3.5 text-left transition-colors hover:bg-[var(--off)] ${
                  index < items.length - 1 ? 'border-b border-[var(--color-border)]' : ''
                }`}
              >
                <div className='flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-[var(--off)]'>
                  <FlagIcon currencyCode={transaction.quoteCurrency} className='h-5 w-7 rounded-sm object-cover' />
                </div>

                <div className='min-w-0'>
                  <p className='truncate text-[13.5px] font-medium text-[var(--n800)]'>{transaction.recipient?.name || '—'}</p>
                  <p className='mt-0.5 truncate text-xs text-[var(--text-tertiary)]'>{subtitle}</p>
                </div>

                <span
                  className='shrink-0 rounded-full px-2.5 py-0.5 text-[11px] font-semibold'
                  style={{ color: status.color, background: `color-mix(in oklab, ${status.color} 10%, transparent)` }}
                >
                  {status.label}
                </span>

                <span className='min-w-[120px] shrink-0 text-right text-sm font-medium tabular-nums text-[var(--n900)] font-[family-name:var(--font-ui)]'>
                  {amountLabel}
                </span>
              </button>
            );
          })}
      </div>
    </section>
  );
}

ActivityFeed.propTypes = {
  transactions: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  loading: PropTypes.bool,
};

ActivityFeed.defaultProps = {
  transactions: [],
  loading: false,
};

export default ActivityFeed;
